import { ProfileForm } from 'components/auth/profile/form';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { TOKEN_KEY } from 'lib/enums/auth';
import { AuthPermissions } from 'lib/interfaces/auth/permissions';
import { UserProfileRequest } from 'lib/interfaces/auth/user-profile';
import { UserWhole } from 'lib/interfaces/database/user';
import { DbUtil } from 'lib/utilities/db';
import { StringUtil } from 'lib/utilities/string';
import {
  createContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AuthService } from 'services/auth';
import { UsersService } from 'services/users';

const REFRESH_DELAY_MIN = 55;

interface State {
  // if cookie is found, whether a token refresh has finished; else true
  init: boolean;
  // whether a token is set in the cookies
  auth: boolean;
  // the logged in user's details
  user: UserWhole | undefined;

  perms: AuthPermissions;
}

interface Actions {
  readonly login: (
    email: string,
    password: string
  ) => Promise<UserWhole | undefined>;
  readonly logout: () => void;
  readonly refreshToken: () => void;

  // undefined => closes any open dialog
  readonly setDialog: (type: DialogType | undefined) => void;
}

interface Context extends State, Actions {}

const DEFAULT: Context = {
  init: false,
  auth: false,
  user: undefined,
  perms: DbUtil.getUserPermissions(undefined),

  login: () => new Promise(() => console.log('AuthContext not init')),
  logout: () => console.log('AuthContext not init'),
  refreshToken: () => console.log('AuthContext not init'),
  setDialog: () => console.log('AuthContext not init'),
};

export const AuthContext = createContext(DEFAULT);

interface Props {
  children: ReactNode;
}

export const AuthProvider: FC<Props> = (props) => {
  const service = AuthService.getInstance();

  const [authToken, setAuthToken] = useState(() => {
    const value = localStorage.getItem(TOKEN_KEY);
    console.debug(`Found value for ${TOKEN_KEY}:\n`, value);
    return value;
  });

  // if we find authToken in cookies, wait for refreshToken to finish before marking as init
  // avoids flickering to login form while waiting on first refresh
  const [init, setInit] = useState(authToken ? false : true);

  const auth = useMemo(() => init && !!authToken, [init, authToken]);
  const [dialog, setDialog] = useState<DialogState>();

  const [user, setUser] = useState(DEFAULT.user);

  const perms = useMemo(() => DbUtil.getUserPermissions(user), [user]);

  const refreshToken = async () => {
    const result = await service.refreshToken(true);

    if (result?.token) {
      console.debug('Setting token from refresh');
      localStorage.setItem(TOKEN_KEY, result.token);
      setAuthToken(result.token);
    } else {
      console.debug('Clearing token from refresh');
      localStorage.removeItem(TOKEN_KEY);
      setAuthToken(null);
    }

    setUser(result?.user);
    setInit(true);
  };

  // 1x attempt to restore from existing cookies at init
  useEffect(() => {
    refreshToken();
  }, []);

  const refreshTimeout = useRef<NodeJS.Timeout>(null);
  // refresh token on a timer
  useEffect(() => {
    // always clear any existing timer
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
    }

    if (!user) {
      console.debug('Skipping token refresh scheduling, no user');
      return;
    }

    // schedule a refresh in the future
    console.debug(
      `Scheduling token refresh for ${REFRESH_DELAY_MIN}min from now`
    );

    refreshTimeout.current = setTimeout(() => {
      refreshToken();
    }, REFRESH_DELAY_MIN * 60 * 1_000);

    return () => {
      if (refreshTimeout.current) {
        clearTimeout(refreshTimeout.current);
      }
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        init: init,
        auth: auth,
        user: user,
        perms: perms,

        login: async (email, password) => {
          const result = await service.login(email, password);

          if (result?.token) {
            console.debug('Setting token from login');
            localStorage.setItem(TOKEN_KEY, result.token);
            setAuthToken(result.token);
          } else {
            console.debug('Clearing token from login');
            localStorage.removeItem(TOKEN_KEY);
            setAuthToken(null);
          }

          setUser(result?.user);
          return result?.user;
        },

        logout: async () => {
          const success = await service.logout();

          if (success) {
            localStorage.removeItem(TOKEN_KEY);
            setAuthToken(null);
            setUser(undefined);
          }

          return success;
        },

        refreshToken: refreshToken,

        setDialog: (type) => {
          if (!type) {
            // close any dialog
            setDialog(undefined);
            return;
          }

          // open a dialog
          setDialog({
            type: type,
            key: Date.now(),
          });
        },
      }}
    >
      {props.children}

      {dialog?.type === DialogType.Update && user && (
        <CommonFormDialog
          title="Update Profile"
          children={<ProfileForm />}
          formData={user}
          submitAction={{
            label: 'Save',
            onClick: async (item: Partial<UserProfileRequest>) => {
              if (!item.password) {
                console.warn(
                  'Current password is required to update the profile'
                );
                return false;
              }

              const newPwdCheck = StringUtil.validPassword(item.newPassword);

              if (item.newPassword && !newPwdCheck.valid) {
                console.warn(`New password invalid: ${newPwdCheck.reason}`);
                return false;
              }

              const result = await UsersService.getInstance().updateSelf(item);

              if (!result) {
                return false;
              }

              // update the user for the session
              setUser(result);
              return true;
            },
          }}
          onClose={() => setDialog(undefined)}
        />
      )}
    </AuthContext.Provider>
  );
};
